import { useEffect, useState } from 'react';
import { Database, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import type { DestinationBucket } from '@atlas/shared';
import { useAuth } from '../auth/AuthProvider.js';
import {
  getDestinationConfig,
  verifyDestinationBucket,
  setDestinationBucket,
} from '../lib/callables.js';

type VerifyState =
  | { status: 'idle' }
  | { status: 'verifying' }
  | { status: 'ok'; region: string }
  | { status: 'error'; message: string };

export function SettingsPage() {
  const { user, userRecord } = useAuth();

  const [current, setCurrent] = useState<DestinationBucket | null>(null);
  const [allowed, setAllowed] = useState<DestinationBucket[]>([]);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [input, setInput] = useState('');
  const [verify, setVerify] = useState<VerifyState>({ status: 'idle' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await getDestinationConfig();
      setCurrent(result.data.current);
      setAllowed(result.data.allowed);
      setUpdatedBy(result.data.updatedBy);
      setInput(result.data.current.bucket);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  if (!userRecord?.isAdmin) {
    return (
      <div className="p-8 text-sm text-[#73726e]">You don't have admin access.</div>
    );
  }

  const trimmed = input.trim();
  const isUnchanged = current != null && trimmed === current.bucket;
  // Save is only enabled once the *current* input value has been verified.
  const canSave =
    verify.status === 'ok' && !isUnchanged && trimmed.length > 0 && !saving;

  function onInputChange(value: string) {
    setInput(value);
    // Any edit invalidates a prior verification.
    setVerify({ status: 'idle' });
    setSaved(false);
  }

  async function handleVerify() {
    if (!trimmed) return;
    setVerify({ status: 'verifying' });
    setSaved(false);
    try {
      const result = await verifyDestinationBucket({ bucket: trimmed });
      if (result.data.ok) {
        setVerify({ status: 'ok', region: result.data.region ?? 'unknown' });
      } else {
        setVerify({
          status: 'error',
          message: result.data.message ?? 'Bucket could not be verified.',
        });
      }
    } catch (err) {
      setVerify({
        status: 'error',
        message: err instanceof Error ? err.message : 'Verification failed.',
      });
    }
  }

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setSaved(false);
    try {
      const result = await setDestinationBucket({ bucket: trimmed });
      setCurrent(result.data.current);
      setUpdatedBy(user?.email ?? null);
      setVerify({ status: 'idle' });
      setSaved(true);
    } catch (err) {
      setVerify({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to save destination.',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-8 py-8">
      <div className="flex items-center gap-2 mb-1">
        <Database size={16} className="text-[#73726e]" />
        <h1 className="text-lg font-semibold text-[#37352f]">Destination</h1>
      </div>
      <p className="text-sm text-[#73726e] mb-6">
        Where new imports are written in S3. Verify a bucket exists before saving;
        existing jobs keep the destination they were created with.
      </p>

      {loadError && (
        <p className="text-xs text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2 mb-4">
          {loadError}
        </p>
      )}

      {loading ? (
        <div className="text-sm text-[#aeaaa4]">Loading…</div>
      ) : (
        <>
          <div className="border border-[#e9e9e7] rounded-lg p-5">
            <div className="mb-4">
              <span className="text-xs font-medium text-[#73726e]">Current destination</span>
              <p className="mt-1 text-sm font-mono text-[#37352f]">
                s3://{current?.bucket}
                <span className="text-[#aeaaa4]"> ({current?.region})</span>
              </p>
              {updatedBy && (
                <p className="text-[10px] text-[#aeaaa4] mt-0.5">Last changed by {updatedBy}</p>
              )}
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-[#73726e]">Destination bucket</span>
              <div className="flex gap-2 items-stretch">
                <input
                  type="text"
                  spellCheck={false}
                  autoComplete="off"
                  placeholder="bucket-name"
                  className="flex-1 px-2.5 py-1.5 text-sm font-mono border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
                  value={input}
                  onChange={(e) => onInputChange(e.target.value)}
                />
                <button
                  type="button"
                  onClick={handleVerify}
                  disabled={!trimmed || verify.status === 'verifying'}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#37352f] border border-[#e9e9e7] rounded hover:bg-[#f1f1ef] disabled:opacity-50 transition-colors"
                >
                  {verify.status === 'verifying' && <Loader2 size={13} className="animate-spin" />}
                  {verify.status === 'verifying' ? 'Verifying…' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSave}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#37352f] rounded hover:bg-[#2e2e2c] disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </label>

            {/* Verify / save feedback */}
            <div className="mt-3 min-h-[20px]">
              {verify.status === 'ok' && (
                <p className="flex items-center gap-1.5 text-xs text-[#0f7b6c]">
                  <CheckCircle2 size={13} />
                  Bucket verified in {verify.region}.{' '}
                  {isUnchanged ? 'This is already the active destination.' : 'You can now save.'}
                </p>
              )}
              {verify.status === 'error' && (
                <p className="flex items-center gap-1.5 text-xs text-[#e03e3e]">
                  <AlertCircle size={13} />
                  {verify.message}
                </p>
              )}
              {saved && verify.status === 'idle' && (
                <p className="flex items-center gap-1.5 text-xs text-[#0f7b6c]">
                  <CheckCircle2 size={13} />
                  Saved. New imports will use s3://{current?.bucket}.
                </p>
              )}
              {verify.status === 'idle' && !saved && !isUnchanged && trimmed.length > 0 && (
                <p className="text-xs text-[#aeaaa4]">Verify this bucket to enable Save.</p>
              )}
            </div>
          </div>

          {/* Approved buckets hint */}
          <div className="mt-5">
            <span className="text-xs font-medium text-[#73726e]">Approved buckets</span>
            <p className="text-[11px] text-[#aeaaa4] mb-2">
              Only these pre-approved buckets can be selected. Adding a new one requires an infra change.
            </p>
            <ul className="flex flex-col gap-1">
              {allowed.map((d) => (
                <li
                  key={d.bucket}
                  className="flex items-center justify-between text-xs font-mono text-[#37352f] border border-[#e9e9e7] rounded px-3 py-1.5"
                >
                  <button
                    type="button"
                    onClick={() => onInputChange(d.bucket)}
                    className="text-left hover:text-[#2383e2] transition-colors"
                    title="Use this bucket"
                  >
                    s3://{d.bucket}
                  </button>
                  <span className="text-[#aeaaa4]">{d.region}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
